import type { CampaignStatus, Installment, InvoiceStatus, LeadSource, LeadStage, MeetingMode, MeetingStatus, NoticeStatus, OnboardingStatus, PartnerStatus, PayMethod, ReferralStatus, Severity, SourceKind, SpendCategory, SubStatus, WebStatus, WebStep } from "../api/types";
import type { Tone } from "./parts";
import { t } from "../i18n";

/* One word and one colour per status, so every screen shows a status the same way. */

export const STAGE_LABEL: Record<LeadStage, string> = {
  new: "New",
  contacted: "Contacted",
  meeting: "Meeting",
  proposal: "Proposal",
  // Older leads can still carry these two stages; the CRM no longer uses the words, so they read as Client / Closed.
  won: "Client",
  lost: "Closed",
};
export const STAGE_TONE: Record<LeadStage, Tone> = { new: "neutral", contacted: "neutral", meeting: "gold", proposal: "amber", won: "green", lost: "red" };

export const SOURCE_LABEL: Record<LeadSource, string> = {
  partner: "Partner",
  campaign: "Campaign",
  website: "Website",
  referral: "Client referral",
  event: "Exhibition / event",
  llm: "AI assistant (LLM)",
  manual: "Added by staff",
};

export const SOURCE_KIND_LABEL: Record<SourceKind, string> = {
  partner: "Partners",
  exhibition: "Exhibitions",
  referral: "Referrals",
  website: "Website",
  llm: "LLM / AI assistants",
};

export const SPEND_LABEL: Record<SpendCategory, string> = {
  tickets: "Tickets / entry",
  travel: "Travel",
  stay: "Stay & food",
  stall: "Stall / booth",
  collateral: "Brochures & giveaways",
  payout: "Partner payout / reward",
  ads: "Ads",
  tools: "Tools & content",
  other: "Other",
};

export const CAMPAIGN_TONE: Record<CampaignStatus, Tone> = { draft: "neutral", scheduled: "gold", running: "green", paused: "amber", completed: "ink", cancelled: "red" };
export const NOTICE_TONE: Record<NoticeStatus, Tone> = { draft: "neutral", scheduled: "gold", live: "green", expired: "neutral", withdrawn: "red" };
export const SEVERITY_TONE: Record<Severity, Tone> = { info: "neutral", warning: "amber", critical: "red" };
export const PARTNER_TONE: Record<PartnerStatus, Tone> = { pending: "amber", active: "green", suspended: "red", closed: "neutral" };
export const PARTNER_LABEL: Record<PartnerStatus, string> = { pending: "Applied", active: "Active", suspended: "Suspended", closed: "Closed" };
// The partner website's status for a referral, shown on the lead.
export const REFERRAL_LABEL: Record<ReferralStatus, string> = { submitted: "Submitted", contacted: "Contacted", converted: "Converted", rejected: "Rejected" };
export const REFERRAL_TONE: Record<ReferralStatus, Tone> = { submitted: "gold", contacted: "amber", converted: "green", rejected: "red" };
// What a wealthoak.ai user has done there, shown on the lead.
export const WEB_LABEL: Record<WebStatus, string> = { interested: "Interested", registered: "Signed up", purchased: "Plan purchased" };
export const WEB_TONE: Record<WebStatus, Tone> = { interested: "gold", registered: "neutral", purchased: "green" };
export const WEB_STEP_LABEL: Record<WebStep, string> = {
  STARTED: "Started sign-up",
  EMAIL_VERIFIED: "Email verified",
  TNC_ACCEPTED: "Terms accepted",
  AGREEMENT_SIGNED: "Agreement signed",
  PAYMENT_STARTED: "Payment started",
  PAYMENT_COMPLETED: "Paid, awaiting review",
  PENDING_REVIEW: "Paid, awaiting review",
  ONBOARDING_COMPLETE: "Approved",
};
/**
 * The Leads sign-up board: one column per step before payment. From the moment payment starts the
 * person is a client and is in Onboarding, so there is no column for it here.
 */
export const SIGNUP_COLUMNS: { key: WebStep | "NEW"; label: string; hint: string }[] = [
  { key: "NEW", label: "New", hint: "Not on the website's sign-up yet" },
  { key: "STARTED", label: "Session started", hint: "Began signing up" },
  { key: "EMAIL_VERIFIED", label: "Email verified", hint: "Confirmed their email" },
  { key: "TNC_ACCEPTED", label: "T&C accepted", hint: "Accepted the terms" },
  { key: "AGREEMENT_SIGNED", label: "Agreement signed", hint: "Next: payment, then Onboarding" },
];
const SIGNUP_KEYS = SIGNUP_COLUMNS.map((c) => c.key);
/** The sign-up column a lead belongs in, or null when they have reached payment and are in Onboarding. */
export const signupColumn = (l: { web_step?: WebStep | null; onboarding_id?: string | null }): WebStep | "NEW" | null => (l.onboarding_id ? null : !l.web_step ? "NEW" : SIGNUP_KEYS.includes(l.web_step) ? l.web_step : null);
// The pill colour of a step on the sign-up board and in the progress list.
export const STEP_DOT: Record<WebStep | "NEW", string> = { NEW: "", STARTED: "is-contacted", EMAIL_VERIFIED: "is-contacted", TNC_ACCEPTED: "is-meeting", AGREEMENT_SIGNED: "is-proposal", PAYMENT_STARTED: "is-won", PAYMENT_COMPLETED: "is-won", PENDING_REVIEW: "is-won", ONBOARDING_COMPLETE: "is-won" };
/** What to show for a website lead: the sign-up step if we have one, else just signed up / plan purchased. */
export function webPill(l: { web_status?: WebStatus | null; web_step?: WebStep | null }): { label: string; tone: Tone } | null {
  if (!l.web_status) return null;
  const tone = WEB_TONE[l.web_status];
  return { label: l.web_step ? WEB_STEP_LABEL[l.web_step] : WEB_LABEL[l.web_status], tone: l.web_status === "registered" && l.web_step ? "gold" : tone };
}
export const INVOICE_TONE: Record<InvoiceStatus, Tone> = { draft: "neutral", issued: "gold", partially_paid: "amber", paid: "green", void: "neutral", credited: "neutral" };
export const ONBOARDING_LABEL: Record<OnboardingStatus, string> = { awaiting_upfront: "Awaiting upfront", balance_due: "Balance due", completed: "Completed", cancelled: "Cancelled" };
export const ONBOARDING_TONE: Record<OnboardingStatus, Tone> = { awaiting_upfront: "amber", balance_due: "gold", completed: "green", cancelled: "neutral" };

/** One word and colour for an instalment, from its status and whether it is late or failed. */
export function installmentPill(i: Installment): { label: string; tone: Tone } {
  switch (i.status) {
    case "paid":
      return { label: t("Paid"), tone: "green" };
    case "cancelled":
      return { label: t("Cancelled"), tone: "neutral" };
    case "sending":
      return { label: t("Sending…"), tone: "gold" };
    case "link_sent":
      return i.is_overdue ? { label: t("Overdue"), tone: "red" } : { label: t("Link sent"), tone: "amber" };
    case "scheduled":
      return i.send_failed ? { label: t("Send failed"), tone: "red" } : { label: t("Scheduled"), tone: "gold" };
    default:
      return i.seq === 1 ? { label: t("Awaiting payment"), tone: "amber" } : { label: t("No date set"), tone: "neutral" };
  }
}

export const PAY_METHODS: { value: PayMethod; label: string }[] = [
  { value: "upi", label: "UPI" },
  { value: "neft", label: "NEFT" },
  { value: "imps", label: "IMPS" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other" },
];
export const payMethodLabel = (m: string | null) => (m === "razorpay" ? t("Online (Razorpay)") : PAY_METHODS.find((x) => x.value === m)?.label ?? m ?? "—");

export const SUB_TONE: Record<SubStatus, Tone> = { trialing: "gold", active: "green", past_due: "red", cancelled: "neutral", expired: "neutral" };
export const MEETING_TONE: Record<MeetingStatus, Tone> = { scheduled: "gold", completed: "green", cancelled: "neutral", no_show: "red" };
export const MEETING_STATUS: Record<MeetingStatus, string> = { scheduled: "Scheduled", completed: "Completed", cancelled: "Cancelled", no_show: "No-show" };
export const MODE_LABEL: Record<MeetingMode, string> = { google_meet: "Google Meet", phone: "Phone call", in_person: "In person" };

